import dayjs from "dayjs";
import type {
  AlertModelRule,
  AlertRule,
  Equipment,
  EquipmentModel,
  FilterCriteria,
} from "../api/equipment/Equipment.typed";

/**
 * Rules whose threshold operator is "<" or "<=" describe a missing-stock condition
 * (alert when too few equipment match the filters). The matched equipment are not
 * themselves anomalous, so we don't highlight their fields. Everything else is treated
 * as an anomaly rule and the matched fields are highlighted.
 */
export function isStockAlertRule(rule: AlertModelRule): boolean {
  const op = String(rule.Operator || "").trim();

  return op === "<" || op === "<=";
}

const RELATIVE_DATE_RE = /^now(?:([+-])(\d+)m)?$/i;

/** Resolves "now", "now-3m", "now+12m" into a YYYY-MM-DD string; passes through other values. */
function resolveDateToken(raw: string): string {
  const match = raw.match(RELATIVE_DATE_RE);
  if (!match) return raw;

  const sign = match[1] === "+" ? 1 : -1;
  const months = match[2] ? parseInt(match[2], 10) : 0;

  return dayjs()
    .add(sign * months, "month")
    .format("YYYY-MM-DD");
}

function likeMatch(value: string, pattern: string, caseInsensitive: boolean): boolean {
  const escaped = pattern.replace(/[.+*?^$(){}|[\]\\]/g, "\\$&");
  const regexSrc = `^${escaped.replace(/%/g, ".*").replace(/_/g, ".")}$`;
  try {
    return new RegExp(regexSrc, caseInsensitive ? "i" : "").test(value);
  } catch {
    return false;
  }
}

function evaluateNumeric(rawValue: unknown, operator: string, target: string): boolean {
  const left = Number(rawValue);
  const right = Number(target);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;

  switch (operator) {
    case "=":
      return left === right;
    case "!=":
    case "<>":
      return left !== right;
    case ">":
      return left > right;
    case "<":
      return left < right;
    case ">=":
      return left >= right;
    case "<=":
      return left <= right;
    default:
      return false;
  }
}

function evaluateDate(rawValue: unknown, operator: string, target: string): boolean {
  const left = dayjs(rawValue == null ? "" : String(rawValue));
  const right = dayjs(resolveDateToken(target));
  if (!left.isValid() || !right.isValid()) return false;

  switch (operator) {
    case "=":
      return left.isSame(right, "day");
    case "!=":
    case "<>":
      return !left.isSame(right, "day");
    case ">":
      return left.isAfter(right, "day");
    case "<":
      return left.isBefore(right, "day");
    case ">=":
      return left.isSame(right, "day") || left.isAfter(right, "day");
    case "<=":
      return left.isSame(right, "day") || left.isBefore(right, "day");
    default:
      return false;
  }
}

function evaluateText(rawValue: unknown, operator: string, target: string): boolean {
  const left = rawValue == null ? "" : String(rawValue);

  switch (operator) {
    case "=":
      return left === target;
    case "!=":
    case "<>":
      return left !== target;
    case "LIKE":
      return likeMatch(left, target, false);
    case "ILIKE":
      return likeMatch(left, target, true);
    case "NOT LIKE":
      return !likeMatch(left, target, false);
    case "NOT ILIKE":
      return !likeMatch(left, target, true);
    default:
      return false;
  }
}

function evaluateCriterion(rawValue: unknown, criterion: FilterCriteria): boolean {
  const operator = String(criterion.Operator || "").trim();
  const target = (criterion.Value ?? "").toString();

  // API convention: empty target with equality-style operator → attribute is empty/missing or its complement.
  if (target === "") {
    const isEmpty = rawValue == null || rawValue === "";
    if (operator === "=" || operator === "ILIKE" || operator === "LIKE") return isEmpty;
    if (
      operator === "!=" ||
      operator === "<>" ||
      operator === "NOT ILIKE" ||
      operator === "NOT LIKE"
    )
      return !isEmpty;
  }

  switch (criterion.Type) {
    case "number":
      return evaluateNumeric(rawValue, operator, target);
    case "date":
    case "period":
      return evaluateDate(rawValue, operator, target);
    default:
      return evaluateText(rawValue, operator, target);
  }
}

/** Client-side mirror of the server's filter evaluation; used to flag anomalous fields. */
export function equipmentMatchesFilters(
  equipment: Equipment,
  filters: NonNullable<AlertModelRule["Filters"]>,
): boolean {
  const criteria = filters.Criteria || [];
  if (criteria.length === 0) return false;

  const logic = filters.Logic || "AND";
  if (logic === "OR") {
    return criteria.some((c) => evaluateCriterion(equipment.Attributes?.[c.AttributeID], c));
  }

  return criteria.every((c) => evaluateCriterion(equipment.Attributes?.[c.AttributeID], c));
}

/** Returns true when a date value falls inside the configured before/after alert window. */
export function isDateAttributeAlerting(rawValue: unknown, alert: AlertRule | undefined): boolean {
  if (!alert) return false;

  const target = dayjs(rawValue == null ? "" : String(rawValue));
  if (!target.isValid()) return false;

  const now = dayjs();

  if (alert.BeforeMonths && alert.BeforeMonths > 0) {
    const threshold = target.subtract(alert.BeforeMonths, "month");
    if (now.isAfter(threshold)) return true;
  }

  if (alert.AfterMonths && alert.AfterMonths > 0) {
    const threshold = target.add(alert.AfterMonths, "month");
    if (now.isAfter(threshold)) return true;
  }

  return false;
}

/**
 * Builds the distinct list of alert labels that apply to a single equipment, merging
 * matched anomaly rules from the model with attribute-level date thresholds.
 * Quantity/stock rules (operator `<` / `<=`) are intentionally excluded.
 */
export function computeEquipmentAlertLabels(
  equipment: Equipment,
  model: EquipmentModel | undefined,
): string[] {
  if (!model) return [];

  const labels: string[] = [];
  const push = (label: string) => {
    const value = label.trim();
    if (value && !labels.includes(value)) labels.push(value);
  };

  for (const attr of model.Attributes || []) {
    if (attr.Type !== "date") continue;
    if (!isDateAttributeAlerting(equipment.Attributes?.[attr.ID], attr.Alert)) continue;

    push(`Échéance: ${attr.Label}`);
  }

  for (const rule of model.Alerts || []) {
    if (!rule.Enabled) continue;
    if (isStockAlertRule(rule)) continue;

    const filters = rule.Filters;
    if (!filters?.Criteria?.length) continue;
    if (!equipmentMatchesFilters(equipment, filters)) continue;

    push(rule.Name || "Alerte");
  }

  return labels;
}
